import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { MoreHorizontal, ChevronRight, FolderOpen, FolderKanban } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from 'components/ui/dropdown-menu'
import Card from '../Card'
import S from '../Card.style'
import useMediaQuery from 'hooks/useMediaQuery'
import * as _ from 'styles/helpers'

export interface ProjectCardWrapperProps {
	linkUrl?: string
	children: any
}

export interface ProjectCardProps {
	className?: string
	name: string
	description?: string
	projectType: string
	locationName: string
	locationCityProv: string
	image: string
	linkUrl?: string
	editUrl?: string
	menuItems?: {
		title: string
		link?: string
		callback?: Function
		hasDividerAfter?: boolean
		isDanger?: boolean
	}[]
	onThreeDotClick?: () => void
	isHorizontal?: boolean
	isOwner?: boolean
	ownerImage?: string
	ownerName?: string
	ownerCompany?: string
	ownerEmail?: string
}

const ProjectCardWrapper = ({ linkUrl, children }: ProjectCardWrapperProps) => {
	if (linkUrl) return <Link to={linkUrl}>{children}</Link>
	return children
}

export const ProjectCard = ({
	className = '',
	name,
	projectType,
	locationName,
	locationCityProv,
	image,
	editUrl,
	menuItems,
	onThreeDotClick,
	linkUrl,
	isHorizontal = false,
	isOwner,
	ownerImage,
	ownerName,
	ownerCompany,
	ownerEmail,
}: ProjectCardProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const [isModalOpen, setIsModalOpen] = useState(false)

	const handleAvatarClick = () => {
		setIsModalOpen(true)
	}

	const handleModalClose = () => {
		setIsModalOpen(false)
	}

	const actions = () => {
		if (editUrl)
			return (
				<button type="button" title="Edit" className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors">
					<MoreHorizontal className="w-5 h-5" />
				</button>
			)
		if (onThreeDotClick) {
			return (
				<>
					{isOwner ? (
						<button type="button" title="Edit" className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors" onClick={onThreeDotClick}>
							<MoreHorizontal className="w-5 h-5" />
						</button>
					) : (
						<Avatar className="cursor-pointer" onClick={handleAvatarClick}><AvatarImage src={ownerImage} /><AvatarFallback /></Avatar>
					)}
				</>
			)
		}
		if (menuItems) {
			return (
				<>
					{isOwner ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button type="button" title="Edit" className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors">
									<MoreHorizontal className="w-5 h-5" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[15ch]">
								{menuItems.map((menuItem, index) => (
									<React.Fragment key={index}>
										<DropdownMenuItem
											className={menuItem.isDanger ? 'text-red-500 focus:text-red-500' : ''}
											onClick={() => menuItem.callback?.()}
										>
											{menuItem.link ? <Link to={menuItem.link} className="w-full">{menuItem.title}</Link> : menuItem.title}
										</DropdownMenuItem>
										{menuItem.hasDividerAfter && <DropdownMenuSeparator />}
									</React.Fragment>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<Avatar className="cursor-pointer" onClick={handleAvatarClick}><AvatarImage src={ownerImage} /><AvatarFallback /></Avatar>
					)}
				</>
			)
		}
	}

	const ownerInfoModal = (
		<Dialog open={isModalOpen} onClose={handleModalClose}>
			<DialogContent className="max-w-[350px] p-8 relative">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<FolderKanban className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Project Owner</DialogTitle>
							<DialogDescription>View project details</DialogDescription>
						</div>
					</div>
				</DialogHeader>
				<div className="flex justify-center mt-2">
					<Avatar className="h-[100px] w-[100px]"><AvatarImage src={ownerImage} /><AvatarFallback /></Avatar>
				</div>
				<h2 className="pt-4 text-xl font-medium text-center">{ownerName}</h2>
				<h2 className="text-xl font-medium text-center">{ownerCompany}</h2>
				<p className="text-sm text-center">
					<a href={`mailto:${ownerEmail}?subject=${encodeURIComponent(name)}`} className="text-blue-500 hover:underline">
						{ownerEmail}
					</a>
				</p>
			</DialogContent>
		</Dialog>
	)

	if (isMobile) {
		return (
			<>
				<div
					data-testid="ProjectCard"
					className={`bg-white rounded-lg shadow-sm overflow-hidden mb-3 ${className}`}
				>
					<Link to={linkUrl || '#'} className="flex items-center px-4 py-3 no-underline text-inherit transition-colors hover:bg-gray-100 active:bg-gray-200">
						<Avatar className="shrink-0 mr-3 h-14 w-14">
							<AvatarImage src={image} />
							<AvatarFallback><FolderOpen className="w-6 h-6" /></AvatarFallback>
						</Avatar>
						<div className="flex-1 min-w-0">
							<h3 className="m-0 mb-0.5 text-base font-semibold text-black truncate">
								{name.length > 28 ? `${name.slice(0, 25)}...` : name}
							</h3>
							<p className="m-0 text-[13px] leading-snug text-gray-700">{locationName}</p>
							<p className="m-0 text-xs leading-snug text-gray-500">{locationCityProv}</p>
						</div>
						<ChevronRight className="shrink-0 w-6 h-6 text-gray-400" />
					</Link>
				</div>
				{ownerInfoModal}
			</>
		)
	}

	return (
		<>
			<Card actions={actions()} isCenteredText>
				<div
					data-testid="ProjectCard"
					className={`w-full [&_a:hover]:no-underline ${className}`}
				>
					<ProjectCardWrapper linkUrl={linkUrl}>
						<div className={isHorizontal ? 'flex' : ''}>
							<div
								className={
									isHorizontal
										? 'flex-[250px_0_0] flex flex-col items-center justify-center -m-4 mr-0 p-4'
										: ''
								}
							>
								<S.Title>{name.length > 20 ? `${name.slice(0, 17)}. . .` : name}</S.Title>{' '}
								<S.Location>{locationName}</S.Location>
								<S.Description>{locationCityProv}</S.Description>
							</div>
							<S.Image
								className={isHorizontal ? '-m-4 ml-0 pt-[25%] flex-1' : ''}
								style={!isHorizontal ? { margin: '1rem -1rem -1rem -1rem' } : undefined}
								src={image}
							/>
						</div>
					</ProjectCardWrapper>
				</div>
			</Card>
			{ownerInfoModal}
		</>
	)
}

export default ProjectCard
